import { useCallback, useEffect, useRef, useState } from 'react';
import { api, downloadFile, errorText, formatDate, formatDateTime, formatMoney, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import type { ArchivedDocument, Partner } from '../types';

interface Finding {
  severity: 'fehler' | 'hinweis';
  field: string;
  text: string;
}

interface SupplierInvoice {
  id: string;
  supplier_id: string | null;
  supplier_partner_name: string | null;
  purchase_order_number: string | null;
  number: string;
  source_format: 'xrechnung_ubl' | 'zugferd_cii';
  source_filename: string;
  source_sha256: string;
  invoice_date: string | null;
  due_date: string | null;
  supplier_name: string | null;
  supplier_vat_id: string | null;
  buyer_reference: string | null;
  order_reference: string | null;
  currency: string;
  net_cents: number;
  tax_cents: number;
  gross_cents: number;
  status: 'eingegangen' | 'freigegeben' | 'abgelehnt' | 'bezahlt';
  findings: Finding[];
  decision_note: string | null;
  decided_at: string | null;
  decided_by_name: string | null;
  received_at: string;
  line_count: number;
}

interface InvoiceLine {
  id: string;
  position: number;
  description: string;
  qty: number;
  unit: string | null;
  unit_price_cents: number | null;
  amount_cents: number;
  tax_category: string | null;
  tax_rate_percent: number | null;
}

interface Summary {
  open: number;
  withErrors: number;
  openCents: number;
  dueSoonCents: number;
}

const FORMAT_LABEL: Record<string, string> = {
  xrechnung_ubl: 'XRechnung (UBL)',
  zugferd_cii: 'ZUGFeRD (CII)',
};

const STATUS_KIND: Record<string, 'default' | 'ok' | 'warn' | 'bad' | 'info'> = {
  eingegangen: 'warn',
  freigegeben: 'ok',
  abgelehnt: 'bad',
  bezahlt: 'info',
};

/** Datei als base64 - denselben Weg nimmt der CSV-Import. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function IncomingPage() {
  const { can } = useAuth();
  const mayDecide = can('admin', 'kaufmann');

  const [invoices, setInvoices] = useState<SupplierInvoice[] | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [suppliers, setSuppliers] = useState<Partner[]>([]);
  const [open, setOpen] = useState<{
    invoice: SupplierInvoice;
    lines: InvoiceLine[];
    archive: ArchivedDocument[];
  } | null>(null);

  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [list, kpi, partnerList] = await Promise.all([
        api<SupplierInvoice[]>('/api/incoming/invoices'),
        api<Summary>('/api/incoming/summary'),
        api<Partner[]>('/api/partners', { query: { role: 'supplier' } }),
      ]);
      setInvoices(list);
      setSummary(kpi);
      setSuppliers(partnerList);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError('');
    setMessage('');

    const angenommen: string[] = [];
    const abgewiesen: string[] = [];

    for (const file of Array.from(files)) {
      try {
        const result = await api<{ invoice: SupplierInvoice; findings: Finding[] }>(
          '/api/incoming/invoices',
          { method: 'POST', body: { filename: file.name, contentBase64: await readAsBase64(file) } },
        );
        const fehler = result.findings.filter((finding) => finding.severity === 'fehler').length;
        angenommen.push(`${result.invoice.number}${fehler > 0 ? ` (${fehler} Fehler)` : ''}`);
      } catch (err) {
        abgewiesen.push(`${file.name}: ${errorText(err)}`);
      }
    }

    if (angenommen.length > 0) setMessage(`Angenommen: ${angenommen.join(', ')}.`);
    if (abgewiesen.length > 0) setError(abgewiesen.join(' — '));
    if (fileInput.current) fileInput.current.value = '';
    setBusy(false);
    await load();
  }

  async function openDetail(id: string) {
    setError('');
    try {
      setOpen(
        await api<{ invoice: SupplierInvoice; lines: InvoiceLine[]; archive: ArchivedDocument[] }>(
          `/api/incoming/invoices/${id}`,
        ),
      );
      setNote('');
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function setSupplier(id: string, supplierId: string) {
    setBusy(true);
    setError('');
    try {
      await api(`/api/incoming/invoices/${id}/assignment`, {
        method: 'PUT',
        body: { supplierId: supplierId || null },
      });
      await load();
      await openDetail(id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function decide(id: string, decision: 'freigegeben' | 'abgelehnt' | 'bezahlt') {
    setBusy(true);
    setError('');
    try {
      await api(`/api/incoming/invoices/${id}/decision`, {
        method: 'POST',
        body: { decision, note: note.trim() || null },
      });
      setMessage(`Rechnung als "${decision}" erfasst.`);
      await load();
      await openDetail(id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!invoices || !summary) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="grid kpi" style={{ marginBottom: 16 }}>
        <div className="kpi-tile">
          <div className="label">Im Eingang</div>
          <div className="value">{summary.open}</div>
          <div className="hint">{formatMoney(summary.openCents)} unentschieden</div>
        </div>
        <div className={summary.withErrors > 0 ? 'kpi-tile alert' : 'kpi-tile'}>
          <div className="label">Mit Fehlern</div>
          <div className="value">{summary.withErrors}</div>
          <div className="hint">nicht freigebbar</div>
        </div>
        <div className="kpi-tile">
          <div className="label">Fällig in 7 Tagen</div>
          <div className="value">{formatMoney(summary.dueSoonCents)}</div>
        </div>
      </div>

      {mayDecide && (
        <Card title="Rechnung empfangen">
          <div className="small muted" style={{ marginBottom: 10 }}>
            XRechnung (XML) oder ZUGFeRD/Factur-X (PDF mit eingebetteter XML). Der Beleg wird
            unverändert ins Archiv gelegt und maschinell geprüft. Mehrere Dateien auf einmal sind
            möglich.
          </div>
          <input
            ref={fileInput}
            type="file"
            accept=".xml,.pdf,application/xml,text/xml,application/pdf"
            multiple
            disabled={busy}
            onChange={(e) => upload(e.target.files)}
          />
        </Card>
      )}

      <Card title="Rechnungseingang" padded={false}>
        {invoices.length === 0 ? (
          <Empty>Noch keine Eingangsrechnungen.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Empfangen</th>
                <th>Nummer</th>
                <th>Lieferant</th>
                <th>Format</th>
                <th>Fällig</th>
                <th className="num">Netto</th>
                <th className="num">Brutto</th>
                <th>Status</th>
                <th>Prüfung</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const fehler = invoice.findings.filter((f) => f.severity === 'fehler').length;
                const hinweise = invoice.findings.length - fehler;
                return (
                  <tr key={invoice.id}>
                    <td className="small muted">{formatDateTime(invoice.received_at)}</td>
                    <td className="mono small">{invoice.number}</td>
                    <td className="small">
                      {invoice.supplier_partner_name ?? (
                        <span className="muted">{invoice.supplier_name ?? '—'} (nicht zugeordnet)</span>
                      )}
                    </td>
                    <td className="small">{FORMAT_LABEL[invoice.source_format]}</td>
                    <td className="small">{invoice.due_date ? formatDate(invoice.due_date) : '—'}</td>
                    <td className="num">{formatMoney(invoice.net_cents)}</td>
                    <td className="num">{formatMoney(invoice.gross_cents)}</td>
                    <td>
                      <Badge kind={STATUS_KIND[invoice.status]}>{invoice.status}</Badge>
                    </td>
                    <td className="small">
                      {fehler > 0 && <Badge kind="bad">{fehler} Fehler</Badge>}
                      {fehler === 0 && hinweise === 0 && <Badge kind="ok">ohne Befund</Badge>}
                      {fehler === 0 && hinweise > 0 && <Badge kind="warn">{hinweise} Hinweis</Badge>}
                    </td>
                    <td className="num">
                      <button className="small ghost" onClick={() => openDetail(invoice.id)}>
                        öffnen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {open && (
        <Card
          title={`Eingangsrechnung ${open.invoice.number}`}
          actions={
            <>
              <button
                className="small"
                onClick={() => downloadFile(`/api/incoming/invoices/${open.invoice.id}/original`)}
              >
                Original laden
              </button>
              <button className="small" onClick={() => setOpen(null)}>
                schliessen
              </button>
            </>
          }
          padded={false}
        >
          <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
            <div className="small muted">
              {open.invoice.supplier_name ?? 'ohne Verkäufernamen'}
              {open.invoice.supplier_vat_id ? ` · ${open.invoice.supplier_vat_id}` : ''}
              {open.invoice.invoice_date ? ` · vom ${formatDate(open.invoice.invoice_date)}` : ''}
              {open.invoice.due_date ? ` · zahlbar bis ${formatDate(open.invoice.due_date)}` : ''}
              {open.invoice.order_reference ? ` · Bestellung ${open.invoice.order_reference}` : ''}
              {open.invoice.purchase_order_number ? ` (zugeordnet: ${open.invoice.purchase_order_number})` : ''}
            </div>
            <div className="small muted" style={{ marginTop: 4 }}>
              {open.invoice.source_filename} · {FORMAT_LABEL[open.invoice.source_format]} · Prüfsumme{' '}
              <span className="mono">{open.invoice.source_sha256.slice(0, 16)}…</span>
            </div>

            {open.invoice.findings.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {open.invoice.findings.map((finding, index) => (
                  <div key={index} className="small" style={{ marginBottom: 4 }}>
                    <Badge kind={finding.severity === 'fehler' ? 'bad' : 'warn'}>{finding.field}</Badge>{' '}
                    {finding.text}
                  </div>
                ))}
              </div>
            )}

            {mayDecide && open.invoice.status === 'eingegangen' && (
              <div style={{ marginTop: 12 }}>
                {!open.invoice.supplier_id && (
                  <label className="field" style={{ maxWidth: 360 }}>
                    <span>Lieferant zuordnen</span>
                    <select
                      value=""
                      onChange={(e) => setSupplier(open.invoice.id, e.target.value)}
                      disabled={busy}
                    >
                      <option value="">— wählen —</option>
                      {suppliers.map((partner) => (
                        <option key={partner.id} value={partner.id}>
                          {partner.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <TextField label="Vermerk" value={note} onChange={setNote} placeholder="optional" />
                <div style={{ marginTop: 8 }}>
                  <button
                    className="primary small"
                    onClick={() => decide(open.invoice.id, 'freigegeben')}
                    disabled={busy}
                  >
                    freigeben
                  </button>{' '}
                  <button className="small" onClick={() => decide(open.invoice.id, 'abgelehnt')} disabled={busy}>
                    ablehnen
                  </button>
                </div>
                <div className="small muted" style={{ marginTop: 6 }}>
                  Eine Rechnung mit Fehlerbefund lässt sich nicht freigeben. Abgelehnt bleibt sie
                  erhalten – der empfangene Beleg gehört ins Archiv, unabhängig davon, ob er
                  bezahlt wird.
                </div>
              </div>
            )}

            {mayDecide && open.invoice.status === 'freigegeben' && (
              <div style={{ marginTop: 12 }}>
                <button
                  className="primary small"
                  onClick={() => decide(open.invoice.id, 'bezahlt')}
                  disabled={busy}
                >
                  als bezahlt erfassen
                </button>
              </div>
            )}

            {open.invoice.decided_at && (
              <div className="small muted" style={{ marginTop: 10 }}>
                {open.invoice.status} am {formatDateTime(open.invoice.decided_at)} durch{' '}
                {open.invoice.decided_by_name ?? 'unbekannt'}
                {open.invoice.decision_note ? ` · ${open.invoice.decision_note}` : ''}
              </div>
            )}
          </div>

          <table>
            <thead>
              <tr>
                <th>Pos</th>
                <th>Bezeichnung</th>
                <th className="num">Menge</th>
                <th>Einheit</th>
                <th className="num">Einzelpreis</th>
                <th>Steuer</th>
                <th className="num">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {open.lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.position}</td>
                  <td>{line.description}</td>
                  <td className="num">{formatQty(line.qty)}</td>
                  <td className="small">{line.unit ?? '—'}</td>
                  <td className="num">
                    {line.unit_price_cents === null ? '—' : formatMoney(line.unit_price_cents)}
                  </td>
                  <td className="small">
                    {line.tax_category ?? '—'}
                    {line.tax_rate_percent !== null ? ` ${line.tax_rate_percent} %` : ''}
                  </td>
                  <td className="num">{formatMoney(line.amount_cents)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={6} style={{ textAlign: 'right' }}>
                  Netto
                </td>
                <td className="num">{formatMoney(open.invoice.net_cents)}</td>
              </tr>
              <tr>
                <td colSpan={6} style={{ textAlign: 'right' }}>
                  Umsatzsteuer
                </td>
                <td className="num">{formatMoney(open.invoice.tax_cents)}</td>
              </tr>
              <tr>
                <td colSpan={6} style={{ textAlign: 'right', fontWeight: 600 }}>
                  Zahlbetrag
                </td>
                <td className="num" style={{ fontWeight: 600 }}>
                  {formatMoney(open.invoice.gross_cents)}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
